import type React from 'react';

import { use, useEffect, useLayoutEffect, useRef } from 'react';
import {
  UNSAFE_DataRouterContext as DataRouterContext,
  UNSAFE_DataRouterStateContext as DataRouterStateContext,
  useLocation,
} from 'react-router';

import { APP_SCROLL_CONTAINER_SELECTOR } from '@/layouts/controllers/ScrollRestoration.constants.ts';

/**
 * The app scrolls an inner content container instead of the window. The
 * container carries a permanent `data-app-scroll-container` marker (see
 * AppLayout / BareLayout) that this mechanism uses to find the scrolling element.
 */
const STORAGE_KEY = 'app-scroll-positions';

const getScrollContainer = (): HTMLElement | null =>
  document.querySelector<HTMLElement>(APP_SCROLL_CONTAINER_SELECTOR);

/**
 * Restores the scroll position of the app content container on history
 * navigation (OCD-301). Plugs the container into the router's scroll
 * restoration mechanism via `router.enableScrollRestoration`: the router
 * records `scrollTop` under each location key on navigation and exposes the
 * position to restore through `restoreScrollPosition` in router state, so
 * POP (back/forward) restores while PUSH starts at the top. Positions are
 * persisted to sessionStorage to survive a full page reload.
 */
export const ScrollRestorationController: React.FC = () => {
  const dataRouterContext = use(DataRouterContext);
  const routerState = use(DataRouterStateContext);
  const location = useLocation();
  const savedPositionsRef = useRef<Record<string, number>>({});

  const restoreScrollPosition = routerState?.restoreScrollPosition;
  const preventScrollReset = routerState?.preventScrollReset;

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  useLayoutEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        savedPositionsRef.current = JSON.parse(stored) as Record<string, number>;
      }
    } catch {
      // unavailable or corrupted storage — start with an empty map
    }
  }, []);

  useEffect(() => {
    const savePositions = () => {
      if (routerState?.navigation.state === 'idle') {
        savedPositionsRef.current[location.key] = getScrollContainer()?.scrollTop ?? 0;
      }
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(savedPositionsRef.current));
      } catch {
        // unavailable storage — positions stay in memory for this session
      }
      window.history.scrollRestoration = 'auto';
    };

    window.addEventListener('pagehide', savePositions);
    return () => window.removeEventListener('pagehide', savePositions);
  }, [location, routerState?.navigation.state]);

  useLayoutEffect(
    () =>
      dataRouterContext?.router.enableScrollRestoration(
        savedPositionsRef.current,
        () => getScrollContainer()?.scrollTop ?? 0,
      ),
    [dataRouterContext],
  );

  useLayoutEffect(() => {
    const container = getScrollContainer();
    if (!container || restoreScrollPosition === false) {
      return;
    }
    if (typeof restoreScrollPosition === 'number') {
      container.scrollTop = restoreScrollPosition;
      return;
    }
    if (location.hash) {
      try {
        document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
      } catch {
        // not a decodable element id — nothing to scroll to
      }
      return;
    }
    if (preventScrollReset) {
      return;
    }
    container.scrollTop = 0;
  }, [location, restoreScrollPosition, preventScrollReset]);

  return null;
};
